import { memo } from 'react';
import { Chip, Highlight, Stack, Typography } from '@common-components';
import { messages } from 'i18n';
import { getDisplayDate, truncateMultiLineText } from 'utils';
import LabelList from 'broker/components/common/LabelList';
import { CarrierFormFieldsNames } from 'broker/dialogs/CarrierDialog/types';
import { CarriersColumns, columnConfig } from './CarriersTableHeader';
import CarriersTableItemMenu from './CarriersTableItemMenu';
import { CarrierOnClick, ExtendedCarrier } from './types';

interface CarriersTableItemProps {
  carrier: ExtendedCarrier;
  onClick: CarrierOnClick;
  searchInput?: string;
  checkedLabels?: string[];
}

function CarriersTableItem({ carrier, onClick, searchInput = '', checkedLabels = [] }: CarriersTableItemProps) {
  const { id, organizationName, domains, purchasedModules, stpConfidenceThreshold, updatedAt } = carrier;

  return (
    <Stack direction="row" px={1.5} alignItems="center" height={(theme) => theme.spacing(9)} width={1} gap={3}>
      <Stack direction="row" flex={columnConfig[CarriersColumns.Carrier].flex} alignItems="center" overflow="hidden">
        <Stack gap={0.5}>
          <Typography
            fontWeight="bold"
            variant="body2"
            sx={{ ...truncateMultiLineText(1), cursor: 'pointer' }}
            onClick={() => onClick({ id })}
          >
            <Highlight searchWords={[searchInput]} textToHighlight={organizationName} />
          </Typography>
        </Stack>
      </Stack>

      <Stack
        flex={columnConfig[CarriersColumns.Domains].flex}
        direction="row"
        overflow="hidden"
        py={1}
        gap={1}
        onClick={() => onClick({ id, scrollTo: CarrierFormFieldsNames.Domain })}
      >
        <LabelList
          variant="contained"
          isRounded
          labels={domains.map((domain) => ({ text: domain.domain }))}
          onAddLabelClick={() => onClick({ id, scrollTo: CarrierFormFieldsNames.Domain })}
          searchInput={searchInput}
          selectedLabels={checkedLabels}
          addButtonText={messages.carriersPage.addDomain}
        />
      </Stack>

      <Stack flex={columnConfig[CarriersColumns.Modules].flex} direction="row" flexWrap="wrap" gap={0.5} py={1}>
        {purchasedModules?.length > 0 ? (
          purchasedModules.map((mod) => (
            <Chip key={mod} label={mod} size="small" color="secondary" variant="outlined" />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </Stack>

      <Stack
        flex={columnConfig[CarriersColumns.StpThreshold].flex}
        alignItems={columnConfig[CarriersColumns.StpThreshold].align}
        overflow="hidden"
      >
        <Typography variant="body2" noWrap>
          {stpConfidenceThreshold != null ? stpConfidenceThreshold : '—'}
        </Typography>
      </Stack>

      {updatedAt && (
        <Stack
          flex={columnConfig[CarriersColumns.LastUpdate].flex}
          alignItems={columnConfig[CarriersColumns.LastUpdate].align}
          overflow="hidden"
        >
          <Typography variant="body2" noWrap>
            {getDisplayDate(updatedAt)}
          </Typography>
        </Stack>
      )}

      <CarriersTableItemMenu extendedCarrier={carrier} onEdit={onClick} />
    </Stack>
  );
}

export default memo(CarriersTableItem);
